import React, { Component } from "react";
import CartContext from "../StoreManager/CartContext";
import {
  Table,
  Button,
  Modal,
  Form,
  Row,
  Col,
  Alert,
  Spinner,
} from "react-bootstrap";

export default class HotelRatePlan extends Component {
  static contextType = CartContext;

  constructor(props) {
    super(props);
    this.state = {
      ratePlans: [],
      loading: false,
      error: null,

      // RatePlan modal
      showPlanModal: false,
      editingPlan: null,
      planModel: this.emptyPlan(),

      // RateRule modal (child lines)
      showRulesModal: false,
      currentPlanId: null,
      rules: [],
      rulesLoading: false,
      showRuleModal: false,
      editingRule: null,
      ruleModel: this.emptyRule(),
      saving: false,
    };
  }

  emptyPlan() {
    return {
      storeId: this.context?.storeId || "",
      code: "",
      name: "",
      rateTypeId: null,
      currencyId: null,
      isActive: true,
    };
  }

  emptyRule() {
    return {
      fromDate: "",
      toDate: "",
      price: "",
      minLOS: "",
      maxLOS: "",
      isClosed: false,
    };
  }

  componentDidMount() {
    this.loadPlans();
  }

  apiUrl(resource) {
    const base = `${process.env.REACT_APP_API}${resource}`;
    const storeId = this.context?.storeId;
    return storeId ? `${base}?storeId=${storeId}` : base;
  }

  fetchWithAuth(url, opts = {}) {
    const headers = opts.headers ? { ...opts.headers } : {};
    const token = this.context?.authToken;
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return fetch(url, { ...opts, headers });
  }

  async parseErrorResponse(res) {
    try {
      const ct = res.headers.get("content-type") || "";
      if (ct.includes("application/json")) {
        const json = await res.json();
        return JSON.stringify(json);
      }
      return await res.text();
    } catch {
      return res.statusText || `Status ${res.status}`;
    }
  }

  // ---------- RatePlan ----------
  async loadPlans() {
    const storeId = this.context?.storeId;
    if (!storeId) {
      this.setState({ error: "StoreId not found in context." });
      return;
    }
    this.setState({ loading: true, error: null });
    try {
      const res = await this.fetchWithAuth(this.apiUrl("HotelRatePlan"));
      if (!res.ok) {
        const body = await this.parseErrorResponse(res);
        throw new Error(`Failed to load rate plans: ${res.status} ${body}`);
      }
      const data = await res.json();
      this.setState({ ratePlans: data });
    } catch (err) {
      console.error(err);
      this.setState({ error: err.message || "Failed to load rate plans." });
    } finally {
      this.setState({ loading: false });
    }
  }

  handleShowPlanModal = (plan = null) => {
    const storeId = this.context?.storeId || "";
    this.setState({
      showPlanModal: true,
      editingPlan: plan,
      planModel: plan
        ? {
            storeId,
            code: plan.code || "",
            name: plan.name || "",
            rateTypeId: plan.rateTypeId || null,
            currencyId: plan.currencyId || null,
            isActive: plan.isActive ?? true,
          }
        : this.emptyPlan(),
      error: null,
    });
  };

  handleClosePlanModal = () =>
    this.setState({ showPlanModal: false, editingPlan: null, error: null });

  handlePlanChange = (e) => {
    const { name, value, type, checked } = e.target;
    this.setState((prev) => ({
      planModel: {
        ...prev.planModel,
        [name]: type === "checkbox" ? checked : value,
      },
    }));
  };

  async handleSavePlan() {
    const { planModel, editingPlan } = this.state;
    const storeId = this.context?.storeId;
    if (!storeId) {
      this.setState({ error: "StoreId not found in context." });
      return;
    }
    if (!planModel.code || !planModel.name) {
      this.setState({ error: "Code and Name are required." });
      return;
    }

    const apiBase = `${process.env.REACT_APP_API}HotelRatePlan`;
    const url = editingPlan
      ? `${apiBase}/${editingPlan.ratePlanId}?storeId=${storeId}`
      : `${apiBase}?storeId=${storeId}`;
    const method = editingPlan ? "PUT" : "POST";

    this.setState({ saving: true, error: null });
    try {
      const res = await this.fetchWithAuth(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(planModel),
      });
      if (!res.ok) {
        const body = await this.parseErrorResponse(res);
        throw new Error(`Save failed: ${res.status} ${body}`);
      }
      this.handleClosePlanModal();
      await this.loadPlans();
    } catch (err) {
      console.error("Error saving plan:", err);
      this.setState({ error: err.message || "Failed to save rate plan." });
    } finally {
      this.setState({ saving: false });
    }
  }

  async handleDeletePlan(id) {
    const storeId = this.context?.storeId;
    if (!storeId) {
      this.setState({ error: "StoreId not found in context." });
      return;
    }
    if (!window.confirm("Delete this rate plan?")) return;

    try {
      const res = await this.fetchWithAuth(
        `${process.env.REACT_APP_API}HotelRatePlan/${id}?storeId=${storeId}`,
        { method: "DELETE" }
      );
      if (!res.ok) {
        const body = await this.parseErrorResponse(res);
        throw new Error(`Delete failed: ${res.status} ${body}`);
      }
      await this.loadPlans();
    } catch (err) {
      console.error("Error deleting plan:", err);
      this.setState({ error: err.message || "Failed to delete rate plan." });
    }
  }

  // ---------- RateRule helpers ----------
  async loadRulesForPlan(ratePlanId) {
    const storeId = this.context?.storeId;
    if (!storeId) {
      this.setState({ error: "StoreId not found in context." });
      return;
    }
    this.setState({
      rulesLoading: true,
      error: null,
      currentPlanId: ratePlanId,
      rules: [],
    });

    try {
      const res = await this.fetchWithAuth(
        `${process.env.REACT_APP_API}HotelRatePlan/${ratePlanId}/rules?storeId=${storeId}`
      );
      if (!res.ok) {
        const body = await this.parseErrorResponse(res);
        throw new Error(`Failed to load rules: ${res.status} ${body}`);
      }
      const data = await res.json();
      this.setState({ rules: data });
    } catch (err) {
      console.error(err);
      this.setState({
        error: err.message || "Failed to load rate rules.",
        rules: [],
      });
    } finally {
      this.setState({ rulesLoading: false });
    }
  }

  // show rules modal (and load)
  async handleShowRulesModal(plan) {
    if (!plan || !plan.ratePlanId) {
      this.setState({ error: "Invalid RatePlan selected." });
      return;
    }
    await this.loadRulesForPlan(plan.ratePlanId);
    this.setState({
      showRulesModal: true,
      showRuleModal: false,
      editingRule: null,
      ruleModel: this.emptyRule(),
    });
  }

  handleCloseRulesModal = () =>
    this.setState({
      showRulesModal: false,
      currentPlanId: null,
      rules: [],
      showRuleModal: false,
      editingRule: null,
      ruleModel: this.emptyRule(),
      error: null,
    });

  handleShowRuleModal = (rule = null) => {
    this.setState({
      showRuleModal: true,
      editingRule: rule,
      ruleModel: rule
        ? {
            fromDate: rule.fromDate ? rule.fromDate.split("T")[0] : "",
            toDate: rule.toDate ? rule.toDate.split("T")[0] : "",
            price: rule.price != null ? String(rule.price) : "",
            minLOS: rule.minLos ?? "",
            maxLOS: rule.maxLos ?? "",
            isClosed: rule.isClosed ?? false,
          }
        : this.emptyRule(),
      error: null,
    });
  };

  handleCloseRuleModal = () =>
    this.setState({
      showRuleModal: false,
      editingRule: null,
      ruleModel: this.emptyRule(),
      error: null,
    });

  handleRuleChange = (e) => {
    const { name, value, type, checked } = e.target;
    this.setState((prev) => ({
      ruleModel: {
        ...prev.ruleModel,
        [name]: type === "checkbox" ? checked : value,
      },
    }));
  };

  async handleSaveRule() {
    const { ruleModel, editingRule, currentPlanId } = this.state;
    const storeId = this.context?.storeId;
    if (!storeId) {
      this.setState({ error: "StoreId not found in context." });
      return;
    }
    if (!currentPlanId) {
      this.setState({ error: "RatePlan not selected." });
      return;
    }

    // validation
    if (!ruleModel.fromDate || !ruleModel.toDate) {
      this.setState({ error: "FromDate and ToDate are required." });
      return;
    }
    if (new Date(ruleModel.toDate) < new Date(ruleModel.fromDate)) {
      this.setState({ error: "ToDate must be on or after FromDate." });
      return;
    }
    const parsedPrice = parseFloat(ruleModel.price);
    if (Number.isNaN(parsedPrice) || parsedPrice <= 0) {
      this.setState({ error: "Price must be a number greater than zero." });
      return;
    }

    const apiBase = `${process.env.REACT_APP_API}HotelRatePlan/${currentPlanId}/rules`;
    const url = editingRule
      ? `${apiBase}/${editingRule.rateRuleId}?storeId=${storeId}`
      : `${apiBase}?storeId=${storeId}`;
    const method = editingRule ? "PUT" : "POST";

    // payload with PascalCase keys that map to server-side model
    const payload = {
      FromDate: ruleModel.fromDate,
      ToDate: ruleModel.toDate,
      Price: parsedPrice,
      StoreId: storeId,
      MinLos: ruleModel.minLOS ? parseInt(ruleModel.minLOS, 10) : null,
      MaxLos: ruleModel.maxLOS ? parseInt(ruleModel.maxLOS, 10) : null,
      IsClosed: !!ruleModel.isClosed,
    };

    this.setState({ saving: true, error: null });
    try {
      const res = await this.fetchWithAuth(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const body = await this.parseErrorResponse(res);
        throw new Error(`Save failed: ${res.status} ${body}`);
      }

      // close rule modal and reload rules using concrete plan id
      this.handleCloseRuleModal();
      await this.loadRulesForPlan(currentPlanId);
    } catch (err) {
      console.error("Error saving rule:", err);
      this.setState({ error: err.message || "Failed to save rule." });
    } finally {
      this.setState({ saving: false });
    }
  }

  async handleDeleteRule(ruleId) {
    const { currentPlanId } = this.state;
    const storeId = this.context?.storeId;
    if (!storeId || !currentPlanId) {
      this.setState({ error: "Missing context (store or plan)." });
      return;
    }
    if (!window.confirm("Delete this rate rule?")) return;

    try {
      const res = await this.fetchWithAuth(
        `${process.env.REACT_APP_API}HotelRatePlan/${currentPlanId}/rules/${ruleId}?storeId=${storeId}`,
        { method: "DELETE" }
      );
      if (!res.ok) {
        const body = await this.parseErrorResponse(res);
        throw new Error(`Delete failed: ${res.status} ${body}`);
      }
      await this.loadRulesForPlan(currentPlanId);
    } catch (err) {
      console.error("Error deleting rule:", err);
      this.setState({ error: err.message || "Failed to delete rule." });
    }
  }

  // ---------- render ----------
  render() {
    const {
      ratePlans,
      loading,
      error,
      showPlanModal,
      planModel,
      editingPlan,
      showRulesModal,
      rules,
      rulesLoading,
      showRuleModal,
      ruleModel,
      saving,
    } = this.state;

    return (
      <div className="container mt-4">
        <h4>Rate Plans</h4>

        {error && <Alert variant="danger">{error}</Alert>}

        <Button className="mb-3" onClick={() => this.handleShowPlanModal()}>
          New Rate Plan
        </Button>

        {loading ? (
          <div>
            <Spinner animation="border" size="sm" /> Loading...
          </div>
        ) : (
          <Table bordered hover responsive>
            <thead className="table-light">
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Type</th>
                <th>Currency</th>
                <th>Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {ratePlans.map((rp) => (
                <tr key={rp.ratePlanId}>
                  <td>{rp.code}</td>
                  <td>{rp.name}</td>
                  <td>{rp.rateType?.code ?? "—"}</td>
                  <td>{rp.currency?.isoCode ?? "—"}</td>
                  <td>{rp.isActive ? "Yes" : "No"}</td>
                  <td>
                    <Button
                      size="sm"
                      variant="outline-secondary"
                      onClick={() => this.handleShowPlanModal(rp)}
                    >
                      Edit
                    </Button>{" "}
                    <Button
                      size="sm"
                      variant="outline-info"
                      onClick={() => this.handleShowRulesModal(rp)}
                    >
                      Rules
                    </Button>{" "}
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => this.handleDeletePlan(rp.ratePlanId)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        {/* RatePlan Modal */}
        <Modal show={showPlanModal} onHide={this.handleClosePlanModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>
              {editingPlan ? "Edit Rate Plan" : "New Rate Plan"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Code</Form.Label>
                <Form.Control
                  name="code"
                  value={planModel.code}
                  onChange={this.handlePlanChange}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  name="name"
                  value={planModel.name}
                  onChange={this.handlePlanChange}
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Rate Type Id (optional)</Form.Label>
                    <Form.Control
                      name="rateTypeId"
                      value={planModel.rateTypeId || ""}
                      onChange={this.handlePlanChange}
                      placeholder="GUID or leave empty"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Currency Id (optional)</Form.Label>
                    <Form.Control
                      name="currencyId"
                      value={planModel.currencyId || ""}
                      onChange={this.handlePlanChange}
                      placeholder="GUID or leave empty"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3" controlId="formIsActive">
                <Form.Check
                  type="checkbox"
                  label="Active"
                  name="isActive"
                  checked={!!planModel.isActive}
                  onChange={this.handlePlanChange}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.handleClosePlanModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => this.handleSavePlan()}
              disabled={saving}
            >
              {saving ? "Saving..." : editingPlan ? "Update" : "Save"}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Rules Modal */}
        <Modal
          show={showRulesModal}
          onHide={this.handleCloseRulesModal}
          size="lg"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Rate Rules</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {rulesLoading ? (
              <div>
                <Spinner animation="border" size="sm" /> Loading rules...
              </div>
            ) : (
              <>
                <Table bordered hover responsive>
                  <thead className="table-light">
                    <tr>
                      <th>From</th>
                      <th>To</th>
                      <th>Price</th>
                      <th>Min LOS</th>
                      <th>Max LOS</th>
                      <th>Closed</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rules.map((r) => (
                      <tr key={r.rateRuleId}>
                        <td>{r.fromDate ? r.fromDate.split("T")[0] : "—"}</td>
                        <td>{r.toDate ? r.toDate.split("T")[0] : "—"}</td>
                        <td>
                          {r.price?.toFixed ? r.price.toFixed(2) : r.price}
                        </td>
                        <td>{r.minLos ?? "—"}</td>
                        <td>{r.maxLos ?? "—"}</td>
                        <td>{r.isClosed ? "Yes" : "No"}</td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            onClick={() => this.handleShowRuleModal(r)}
                          >
                            Edit
                          </Button>{" "}
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => this.handleDeleteRule(r.rateRuleId)}
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                <Button
                  className="mb-2"
                  onClick={() => this.handleShowRuleModal()}
                >
                  Add Rule
                </Button>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.handleCloseRulesModal}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Rule Modal */}
        <Modal show={showRuleModal} onHide={this.handleCloseRuleModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>
              {this.state.editingRule ? "Edit Rule" : "Add Rule"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>From Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="fromDate"
                      value={ruleModel.fromDate}
                      onChange={this.handleRuleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>To Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="toDate"
                      value={ruleModel.toDate}
                      onChange={this.handleRuleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Price</Form.Label>
                    <Form.Control
                      type="number"
                      name="price"
                      value={ruleModel.price}
                      onChange={this.handleRuleChange}
                      step="0.01"
                      min="0"
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Min LOS</Form.Label>
                    <Form.Control
                      type="number"
                      name="minLOS"
                      value={ruleModel.minLOS ?? ""}
                      onChange={this.handleRuleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Max LOS</Form.Label>
                    <Form.Control
                      type="number"
                      name="maxLOS"
                      value={ruleModel.maxLOS ?? ""}
                      onChange={this.handleRuleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Closed"
                  name="isClosed"
                  checked={!!ruleModel.isClosed}
                  onChange={this.handleRuleChange}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={this.handleCloseRuleModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => this.handleSaveRule()}
              disabled={saving}
            >
              {saving ? "Saving..." : this.state.editingRule ? "Update" : "Add"}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }
}
